"use client";

import { cn } from "@repo/design-system/lib/utils";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import type { TourStepDefinition } from "./types";
import { TOUR_CARD_WIDTH, usePlacement } from "./use-placement";
import { useTargetRect } from "./use-target-rect";

interface TourCardProps {
  isFirst: boolean;
  isLast: boolean;
  onClose: () => void;
  onFinish: () => void;
  onNext: () => void;
  onPrev: () => void;
  onShown: () => void;
  step: TourStepDefinition;
  stepIndex: number;
  totalSteps: number;
}

export function TourCard({
  step,
  stepIndex,
  totalSteps,
  isLast,
  onNext,
  onClose,
  onFinish,
  onShown,
}: TourCardProps) {
  const rect = useTargetRect(step.target);
  const placement = usePlacement(rect, step.placement ?? "bottom");
  const cardRef = useRef<HTMLDivElement>(null);
  const isPlaced = Boolean(placement);

  useAdvanceOnTargetClick(step.target, isLast ? onFinish : onNext);
  useKeyboardClose(onClose);
  useMarkShown(isPlaced, onShown);

  if (!placement) {
    return null;
  }

  const current = step.progress?.current ?? stepIndex + 1;
  const total = step.progress?.total ?? totalSteps;

  return (
    <div
      aria-describedby="trayn-tour-content"
      aria-labelledby="trayn-tour-title"
      aria-modal="true"
      className={cn(
        "fixed z-10000 flex flex-col gap-1.5 rounded-2xl bg-popover px-4 py-3 text-popover-foreground",
        "shadow-[0_8px_28px_-10px_hsl(var(--primary)/0.22),0_2px_10px_-4px_hsl(var(--foreground)/0.18)]"
      )}
      data-trayn-tour-card="true"
      ref={cardRef}
      role="dialog"
      style={{
        top: placement.cardTop,
        left: placement.cardLeft,
        width: TOUR_CARD_WIDTH,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary/80">
          {current} · {total}
        </span>
        <button
          aria-label="Close tour"
          className="-mr-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          onClick={onClose}
          type="button"
        >
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      </div>
      <h2
        className="text-[14px] font-semibold leading-tight tracking-tight text-foreground"
        id="trayn-tour-title"
      >
        {step.title}
      </h2>
      <p
        className="text-[12px] leading-relaxed text-muted-foreground"
        id="trayn-tour-content"
      >
        {step.content}
      </p>
    </div>
  );
}

function useAdvanceOnTargetClick(target: string, advance: () => void) {
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const node = event.target;
      if (!(node instanceof Element)) {
        return;
      }
      if (node.closest(target)) {
        advance();
      }
    };
    window.addEventListener("click", handler, true);
    return () => window.removeEventListener("click", handler, true);
  }, [target, advance]);
}

function useKeyboardClose(onClose: () => void) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
}

function useMarkShown(isPlaced: boolean, onShown: () => void) {
  useEffect(() => {
    if (isPlaced) {
      onShown();
    }
  }, [isPlaced, onShown]);
}
